import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { G, Card, Stat, KV, Mono, Row, Dot, Lockup, Glyph, Badge, NumBadge, Chevron, Flywheel, Spokes, LIME, GREEN, GREEN_2, OFFWHITE, INK } from "./kit";

export const PitchLink = ({ style }) => (
  <Link
    to="/pitch"
    className="gtm-xlink an"
    style={{ "--d": "700ms", ...style }}
    data-testid="gtm-pitch-link"
  >
    SEE THE INVESTOR PITCH <ArrowUpRight size={12} />
  </Link>
);

/* 01 — cover */
const S1 = () => (
  <G n={1} tagline="GO-TO-MARKET · 2026">
    <div style={{ position: "absolute", left: 512, top: 48, width: 448, height: 448 }}>
      {[224, 168, 112].map((r, i) => (
        <div key={r} className="an-s" style={{ position: "absolute", left: 224 - r, top: 224 - r, width: r * 2, height: r * 2, borderRadius: 999, background: ["#101d18", "#13231d", "#172820"][i], "--d": `${180 + i * 110}ms` }} />
      ))}
      <div className="an-s" style={{ position: "absolute", left: 170, top: 170, width: 108, height: 108, borderRadius: 26, background: LIME, display: "grid", placeItems: "center", boxShadow: "0 24px 60px rgba(220,255,58,.18)", "--d": "520ms" }}>
        <svg viewBox="0 0 32 32" width={74} height={74} fill="none">
          <path d="M7 11 L15 23 L27 4" stroke={INK} strokeWidth={5.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
    <div style={{ position: "absolute", left: 43.2, top: 150, width: 560 }}>
      <Mono size={10} color={LIME} ls="0.16em" style={{}}>GO-TO-MARKET STRATEGY · SEED ROUND · 2026</Mono>
      <h1 className="h" style={{ fontSize: 50, lineHeight: "60px", position: "absolute", top: 30, left: 0, width: 560 }}>
        <span className="an" style={{ display: "block", whiteSpace: "nowrap", "--d": "160ms" }}>The modern operating</span>
        <span className="an lime" style={{ display: "block", whiteSpace: "nowrap", "--d": "250ms" }}>system for wholesale.</span>
      </h1>
      <div className="serif an" style={{ fontSize: 28, position: "absolute", top: 178, color: "#e8efe9", "--d": "420ms" }}>Wholesale, instant.</div>
      <div className="an" style={{ width: 82, height: 3, background: LIME, position: "absolute", top: 228, "--d": "500ms" }} />
      <div className="m an" style={{ fontSize: 11, position: "absolute", top: 252, color: "#9fb0a6", whiteSpace: "nowrap", "--d": "580ms" }}>
        Zev Ostreicher · Founder &amp; CEO · verqo.com
      </div>
      <PitchLink style={{ position: "absolute", top: 284 }} />
    </div>
  </G>
);

/* 02 — thesis */
const THESIS = [
  { k: "01 · LAND", t: "BRND DIRECT + Jewish wholesale", s: "our buyers, our suppliers, our community" },
  { k: "02 · MONETIZE", t: "Payments · financing · shipping", s: "the money behind each order" },
  { k: "03 · EXPAND", t: "Partners bring sellers", s: "insurance · shipping · 3PL · CFOs" },
];
const COMPS = [
  ["Faire", "net terms → our financing"],
  ["Bill.com", "accountants → our CFO partners"],
  ["Brex", "one segment first"],
  ["Melio", "free side, paid side"],
];
const S2 = () => (
  <G n={2} eyebrow="THE GTM THESIS" size={24}
    title={<>Land our own network. <span className="lime">Monetize the money. Partners bring the next seller.</span></>}>
    <Flywheel cx={318} cy={300} r={96} hub={66} label="Monthly" sub="GMV" d={240} />
    {/* 01 LAND — above the wheel, 02 MONETIZE — lower right, 03 EXPAND — left */}
    <div className="an" style={{ position: "absolute", left: 158, top: 116, width: 320, textAlign: "center", "--d": "300ms" }}>
      <Mono size={9} color={LIME}>{THESIS[0].k}</Mono>
      <div className="h" style={{ fontSize: 15, marginTop: 10 }}>{THESIS[0].t}</div>
      <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 7 }}>{THESIS[0].s}</div>
    </div>
    <div className="an" style={{ position: "absolute", left: 404, top: 372, width: 250, "--d": "430ms" }}>
      <Mono size={9} color={LIME}>{THESIS[1].k}</Mono>
      <div className="h" style={{ fontSize: 15, marginTop: 10 }}>{THESIS[1].t}</div>
      <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 7 }}>{THESIS[1].s}</div>
    </div>
    <div className="an" style={{ position: "absolute", left: 24, top: 372, width: 196, textAlign: "right", "--d": "560ms" }}>
      <Mono size={9} color={LIME}>{THESIS[2].k}</Mono>
      <div className="h" style={{ fontSize: 15, marginTop: 10 }}>{THESIS[2].t}</div>
      <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 7 }}>{THESIS[2].s}</div>
    </div>
    <Card d={300} accent style={{ position: "absolute", left: 672, top: 126, width: 244.8, height: 306 }}>
      <Mono size={9} color={LIME}>SAME PLAYBOOK AS</Mono>
      <div style={{ marginTop: 16 }}>
        {COMPS.map(([n, s], i) => (
          <Row key={n} className="an" style={{ marginBottom: 20, gap: 11, alignItems: "flex-start", "--d": `${420 + i * 90}ms` }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, border: `2.6px solid ${LIME}`, marginTop: 4, flex: "0 0 auto" }} />
            <div>
              <div className="h" style={{ fontSize: 13 }}>{n}</div>
              <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 5 }}>{s}</div>
            </div>
          </Row>
        ))}
      </div>
    </Card>
  </G>
);

/* 03 — what Verqo does */
const FOUR = [
  { n: "01", g: "ring", k: "BUYER CREDIT VISIBILITY", t: "Know your buyer", s: "who they are and what credit they have" },
  { n: "02", g: "bolt", k: "PAYMENTS + INSTANT PAYOUTS", t: "Get paid instantly", s: "buyers pay on ACH, wire or card; sellers get the money instantly (RTP / FedNow)" },
  { n: "03", g: "cube", k: "SHIPPING & INSURANCE", t: "Ship and insure", s: "built in, at cheaper, competitive rates" },
  { n: "04", g: "arrowUp", k: "FINANCING THE ORDER", t: "Buy now, pay later", s: "buyers get Net terms and loans; the seller is still paid today. Live 1–2 months in" },
];
const S3 = () => (
  <G n={3} eyebrow="WHAT VERQO DOES" size={30}
    title={<>Four things every wholesale order needs. <span className="lime">One place.</span></>}>
    <Row style={{ position: "absolute", left: 43.2, top: 130, gap: 12.8 }}>
      {FOUR.map((f, i) => (
        <Card key={f.n} d={190 + i * 95} accent={i === 3} style={{ width: 209.4, height: 230 }}>
          <Row style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            <Glyph kind={f.g} size={38} d={300 + i * 95} />
            <div className="h" style={{ fontSize: 26, color: i === 3 ? LIME : "#2f4a40" }}>{f.n}</div>
          </Row>
          <Mono size={8} color="#7d8f85" style={{ marginTop: 16 }}>{f.k}</Mono>
          <div className="h" style={{ fontSize: 19, marginTop: 10 }}>{f.t}</div>
          <div style={{ fontSize: 11.5, color: "#9fb0a6", marginTop: 12, lineHeight: "16px" }}>{f.s}</div>
        </Card>
      ))}
    </Row>
    {[0, 1, 2].map((i) => (
      <Chevron key={i} d={620 + i * 90} style={{ position: "absolute", left: 249 + i * 222.2, top: 233 }} />
    ))}
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 61.2, top: 408 }}>
      REVENUE OVER 24 MONTHS IN THE PLAN · ON TOP OF $4.30M OF SUBSCRIPTIONS
    </Mono>
    <Row style={{ position: "absolute", left: 61.2, top: 428, width: 860, gap: 0 }}>
      {[["$6.61M", "buy now, pay later"], ["$4.32M", "payments & payouts"], ["$4.61M", "shipping"], ["$172K", "insurance"]].map(([v, l], i) => (
        <Row key={l} className="an" style={{ width: 216, gap: 9, alignItems: "baseline", "--d": `${560 + i * 90}ms` }}>
          <span className="h lime" style={{ fontSize: 17 }}>{v}</span>
          <span style={{ fontSize: 10.5, color: "#9fb0a6" }}>{l}</span>
        </Row>
      ))}
    </Row>
  </G>
);

/* 04 — six tools */
const SIX = [
  ["Dropship apps", "list and fulfil through retail partners", "arrowRight"],
  ["Catalog management", "one catalog, every buyer and channel", "note"],
  ["Find suppliers & buyers", "discover who to buy from and sell to", "ring"],
  ["Banking for wholesale", "accounts and money movement", "flag"],
  ["Corporate & virtual cards", "issue cards for spend and payables", "note"],
  ["Integrations", "QuickBooks · Shopify · EDI", "hexagon"],
];
const S4 = () => (
  <G n={4} eyebrow="PLUS EVERYTHING WHOLESALE NEEDS" size={30}
    title={<>Six tools today. <span className="lime">One portal on Verqo.</span></>}>
    <Spokes
      hub={[480, 326]}
      d={480}
      points={[[306.6, 182], [306.6, 293], [306.6, 404], [653.4, 182], [653.4, 293], [653.4, 404]]}
    />
    {SIX.map(([t, s, g], i) => (
      <Card key={t} d={200 + i * 85} style={{ position: "absolute", left: i < 3 ? 61.2 : 653.4, top: 136 + (i % 3) * 111, width: 245.4, height: 92 }}>
        <Row style={{ gap: 13, alignItems: "flex-start" }}>
          <Glyph kind={g} size={30} d={330 + i * 85} style={{ flex: "0 0 auto", marginTop: 2 }} />
          <div>
            <div className="h" style={{ fontSize: 14 }}>{t}</div>
            <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 9, lineHeight: "14px" }}>{s}</div>
          </div>
        </Row>
      </Card>
    ))}
    <div className="vpd-card-a an-s" style={{ position: "absolute", left: 356, top: 214, width: 248, height: 224, padding: "22px 24px", boxSizing: "border-box", "--d": "260ms" }}>
      <Row style={{ gap: 11 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: LIME, display: "grid", placeItems: "center" }}>
          <svg viewBox="0 0 32 32" width={24} height={24} fill="none">
            <path d="M7 11 L15 23 L27 4" stroke={INK} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="h" style={{ fontSize: 20 }}>verqo</span>
      </Row>
      <div className="h an" style={{ fontSize: 17, marginTop: 30, "--d": "520ms" }}>One portal.</div>
      <div className="h an lime" style={{ fontSize: 17, marginTop: 5, "--d": "580ms" }}>One source of truth.</div>
      <div className="serif an" style={{ fontSize: 11, marginTop: 20, color: LIME, "--d": "640ms" }}>easy to run, easy to operate</div>
    </div>
  </G>
);

/* 05 — why now */
const WHYNOW = [
  { g: "bolt", n: "01", t: "Bank rails became APIs", s: "RTP 2017 · FedNow 2023" },
  { g: "badge", n: "02", t: "Buyers expect digital terms", s: "Faire taught retailers Net 60" },
  { g: "star", n: "03", t: "AI makes credit cheap", s: "small invoices now worth underwriting" },
];
const S5 = () => (
  <G n={5} eyebrow="WHY NOW" size={30}
    title={<>Wholesale still runs on email and PDFs. <span className="lime">Three things just changed.</span></>}>
    <Row style={{ position: "absolute", left: 43.2, top: 170, gap: 14 }}>
      {WHYNOW.map((w, i) => (
        <Card key={w.n} d={220 + i * 120} accent={i === 2} style={{ width: 281.8, height: 258, padding: "25px 26px" }}>
          <Row style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            {w.g === "badge"
              ? <Badge size={46} d={340 + i * 120} />
              : <Glyph kind={w.g} size={46} d={340 + i * 120} />}
            <span className="h" style={{ fontSize: 30, color: "#24392f" }}>{w.n}</span>
          </Row>
          <div className="h" style={{ fontSize: 21, marginTop: 50, lineHeight: "27px" }}>{w.t}</div>
          <div style={{ fontSize: 12.5, color: "#9fb0a6", marginTop: 26 }}>{w.s}</div>
        </Card>
      ))}
    </Row>
  </G>
);

/* 06 — traction */
const S6 = () => (
  <G n={6} eyebrow="TRACTION · CUSTOMER #1 IS LIVE" size={32}
    title={<>BRND DIRECT is user #1. <span className="lime">Ten beta users are next.</span></>}>
    <Card d={190} accent style={{ position: "absolute", left: 43.2, top: 128, width: 873.6, height: 100, padding: "20px 25px" }}>
      <Row style={{ alignItems: "flex-start" }}>
        <div style={{ width: 460 }}>
          <Mono size={9} color={LIME}>THE NUMBER THAT MATTERS</Mono>
          <div className="h" style={{ fontSize: 44, marginTop: 10 }}>$40K / month</div>
        </div>
        <div style={{ fontSize: 13, color: "#cfdcd4", lineHeight: "19px", paddingTop: 16 }}>
          BRND DIRECT’s monthly sales running through Verqo today, across its 3 customers.
        </div>
      </Row>
    </Card>
    <div style={{ position: "absolute", left: 43.2, top: 252 }}>
      <Mono size={9} color="#7d8f85">BRND DIRECT + ITS CURRENT CUSTOMERS · LIVE ON VERQO</Mono>
      <Row style={{ marginTop: 26, width: 556 }}>
        {[["2", "orders per month"], ["3", "customers ordering on Verqo"], ["3", "channels they sell on: Amazon, TikTok Shop, Whatnot"]].map(([v, l], i) => (
          <div key={l} className="an" style={{ width: 186, paddingRight: 18, "--d": `${300 + i * 110}ms` }}>
            <div className="h" style={{ fontSize: 40 }}>{v}</div>
            <div style={{ fontSize: 12, color: "#9fb0a6", marginTop: 20, lineHeight: "16px" }}>{l}</div>
          </div>
        ))}
      </Row>
    </div>
    <div style={{ position: "absolute", left: 615.6, top: 252, width: 301.2 }}>
      <Mono size={9} color="#7d8f85">WHO BUYS THROUGH VERQO TODAY</Mono>
      {[["Amazon vendor", "sells to Amazon wholesale"], ["TikTok Shop seller", "social commerce"], ["Whatnot seller", "live-shopping marketplace"]].map(([n, s], i) => (
        <div key={n} className="an" style={{ marginTop: i ? 22 : 24, "--d": `${360 + i * 100}ms` }}>
          <Row style={{ gap: 8, alignItems: "baseline" }}>
            <span className="h" style={{ fontSize: 13 }}>{n}</span>
            <span style={{ fontSize: 10.5, color: "#8ea096" }}>· {s}</span>
          </Row>
        </div>
      ))}
      <div className="serif an" style={{ fontSize: 12, marginTop: 28, color: LIME, "--d": "700ms" }}>
        Next: 10 free beta seats for friends &amp; family
      </div>
    </div>
  </G>
);

/* 07 — the wedge (funnel) */
/* Printed as concentric rings, widest first, with a leader line out to each
   label. Order in the data is innermost (BRND, live today) outward. */
const WEDGE = [
  { t: "BRND DIRECT + its customers", s: "user #1, live", r: 22, c: INK },
  { t: "10 free beta users", s: "Months 1–3, 7 convert", r: 54, c: LIME },
  { t: "Zev’s 30+ personal network", s: "Day 31–90 → 16 sellers by M3", r: 88, c: "#9ed34a" },
  { t: "Jewish private-label + F&B · beauty", s: "from M3 → 116 sellers by M12", r: 120, c: GREEN_2 },
  { t: "Partner-referred sellers", s: "Months 4–24 → 346 sellers by M24", r: 152, c: "#1b382f" },
];
const S7 = () => {
  const cx = 236;
  const cy = 300;
  return (
    <G n={7} eyebrow="THE WEDGE · WHO FIRST" size={28}
      title={<>Who first: our own buyers, <span className="lime">then a community we belong to.</span></>}>
      {[...WEDGE].reverse().map((w, i) => (
        <div key={w.t} className="an-s" style={{ position: "absolute", left: cx - w.r, top: cy - w.r, width: w.r * 2, height: w.r * 2, borderRadius: 999, background: w.c, "--d": `${200 + i * 110}ms` }} />
      ))}
      <svg width="960" height="540" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
        {[...WEDGE].reverse().map((w, i) => {
          const ty = 136 + i * 74;
          // Anchor on the ring the label names, fanned so the lines never cross.
          const ang = (-74 + i * 16) * (Math.PI / 180);
          const x = cx + (w.r - 5) * Math.cos(ang);
          const y = cy + (w.r - 5) * Math.sin(ang);
          return (
            <g key={w.t}>
              <line x1={x} y1={y} x2={414} y2={ty} stroke="rgba(255,255,255,.3)" strokeWidth="1"
                className="dash" style={{ "--len": 340, "--d": `${420 + i * 100}ms` }} />
              <circle cx={x} cy={y} r="3.4" fill={i === WEDGE.length - 1 ? LIME : INK} className="an-s"
                style={{ "--d": `${520 + i * 100}ms`, transformOrigin: `${x}px ${y}px` }} />
            </g>
          );
        })}
      </svg>
      {[...WEDGE].reverse().map((w, i) => (
        <div key={w.t} className="an" style={{ position: "absolute", left: 424, top: 124 + i * 74, width: 240, "--d": `${480 + i * 100}ms` }}>
          <div className="h" style={{ fontSize: 13, lineHeight: "17px" }}>{w.t}</div>
          <div className="m" style={{ fontSize: 9.5, color: "#8ea096", marginTop: 7, letterSpacing: "0.06em" }}>{w.s}</div>
        </div>
      ))}
    <Card d={300} accent style={{ position: "absolute", left: 672, top: 126, width: 244.8, height: 322, padding: "20px 22px" }}>
      <Mono size={9} color={LIME}>WHY THEM: WE ARE THEM</Mono>
      {[["15 years", "in wholesale"], ["$30M+", "in wholesale revenue"], ["300+", "suppliers & buyers we know personally"]].map(([v, l], i) => (
        <div key={l} className="an" style={{ marginTop: i ? 34 : 24, "--d": `${420 + i * 110}ms` }}>
          <div className="h" style={{ fontSize: 24 }}>{v}</div>
          <div style={{ fontSize: 10, color: "#8ea096", marginTop: 9, lineHeight: "13px" }}>{l}</div>
        </div>
      ))}
    </Card>
    </G>
  );
};

/* 08 — market sizing */
const TIERS = [
  { k: "TAM", v: "$8.4T", s: "U.S. wholesale trade, 2025", r: 128 },
  { k: "SAM", v: "$2.3T", s: "U.S. B2B sold online, 2024", r: 96 },
  { k: "WEDGE", v: "$110B", s: "U.S. beauty & personal care, 2025", r: 60 },
  { k: "SOM · Y2", v: "$4.2B", s: "Year-2 GMV in the plan, 346 sellers", r: 28, lime: true },
];
const S8 = () => (
  <G n={8} eyebrow="MARKET SIZING · BOTTOM-UP" size={30}
    title={<>An $8.4T market. <span className="lime">We start in our own address book.</span></>}>
    <div style={{ position: "absolute", left: 0, top: 140, width: 440, height: 300 }}>
      {TIERS.map((t, i) => (
        <div key={t.k} className="an-s" style={{ position: "absolute", left: 220 - t.r + i * 26, top: 290 - t.r * 2, width: t.r * 2, height: t.r * 2, borderRadius: 999, background: t.lime ? LIME : ["#16302a", "#1d4238", GREEN][i], "--d": `${200 + i * 110}ms` }} />
      ))}
    </div>
    {/* leader lines from the top of each ring out to its figure, as printed */}
    <svg width="960" height="540" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
      {TIERS.map((t, i) => {
        const x = 220 + i * 26;
        const y = 430 - t.r * 2 + 10;
        const ty = 150 + i * 72;
        return (
          <g key={t.k}>
            <line x1={x} y1={y} x2={446} y2={ty} stroke="rgba(255,255,255,.26)" strokeWidth="1"
              className="dash" style={{ "--len": 320, "--d": `${420 + i * 100}ms` }} />
            <circle cx={x} cy={y} r="3.4" fill={t.lime ? LIME : "#cfdcd4"} className="an-s"
              style={{ "--d": `${520 + i * 100}ms`, transformOrigin: `${x}px ${y}px` }} />
          </g>
        );
      })}
    </svg>
    <div style={{ position: "absolute", left: 453.6, top: 128 }}>
      {TIERS.map((t, i) => (
        <Row key={t.k} className="an" style={{ height: 72, alignItems: "baseline", "--d": `${320 + i * 100}ms` }}>
          <div style={{ width: 151.2 }}>
            <Mono size={9} color={t.lime ? LIME : "#7d8f85"}>{t.k}</Mono>
            <div className={`h ${t.lime ? "lime" : ""}`} style={{ fontSize: 24, marginTop: 8 }}>{t.v}</div>
          </div>
          <div style={{ fontSize: 11, color: "#9fb0a6", width: 300 }}>{t.s}</div>
        </Row>
      ))}
    </div>
    <div className="an" style={{ position: "absolute", left: 615.6, top: 402, "--d": "760ms" }}>
      <Mono size={9} color="#7d8f85">BOTTOM-UP CHECK</Mono>
      <div style={{ fontSize: 11.5, fontWeight: 700, marginTop: 12 }}>Oct ’28: 346 sellers → $524M GMV / month</div>
    </div>
  </G>
);

/* 09 — zero to $1B */
const WINNERS = [
  ["Brex", 1.5, "card for VC-backed startups"],
  ["Ramp", 2.1, "free card, 1.5% back"],
  ["Faire", 2.5, "free buyers + net 60"],
  ["Melio", 2.8, "free bill pay for SMBs"],
  ["Mercury", 4.3, "bank accounts for startups"],
  ["Toast*", 8.0, "restaurant POS first"],
  ["Bill.com*", 13.0, "AP/AR via accountants"],
];
const S9 = () => (
  <G n={9} eyebrow="HOW THE WINNERS DID IT · ZERO TO $1B" size={30}
    title={<>The fastest hit $1B in 18 months. <span className="lime">All started narrow.</span></>}>
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 110 }}>YEARS FROM FOUNDING TO A $1B+ VALUATION</Mono>
    <div style={{ position: "absolute", left: 43.2, top: 136, width: 560 }}>
      {WINNERS.map(([n, y], i) => (
        <Row key={n} style={{ height: 47 }}>
          <span className="an" style={{ width: 62, textAlign: "right", fontSize: 11, color: "#cfdcd4", paddingRight: 12, "--d": `${200 + i * 70}ms` }}>{n}</span>
          <div className="gw" style={{ height: 20, borderRadius: 4, background: i < 2 ? LIME : i < 5 ? GREEN_2 : "#1b3c33", "--w": `${(y / 13) * 390}px`, "--d": `${240 + i * 70}ms` }} />
          <span className="m an" style={{ fontSize: 10, color: "#9fb0a6", marginLeft: 10, "--d": `${420 + i * 70}ms` }}>{y.toFixed(1)} yrs</span>
        </Row>
      ))}
    </div>
    <Card d={320} accent style={{ position: "absolute", left: 648, top: 128, width: 268.8, height: 322, padding: "18px 20px" }}>
      <Mono size={9} color={LIME}>THE WEDGE EACH ONE PICKED</Mono>
      <div style={{ marginTop: 14 }}>
        {WINNERS.map(([n, , w], i) => (
          <Row key={n} className="an" style={{ height: 41.8, alignItems: "baseline", "--d": `${420 + i * 60}ms` }}>
            <span className="h" style={{ fontSize: 11, width: 76 }}>{n.replace("*", "")}</span>
            <span style={{ fontSize: 10, color: "#8ea096" }}>{w}</span>
          </Row>
        ))}
      </div>
    </Card>
  </G>
);

/* 10 — Faire */
const FAIRE = [
  ["2017", "Founded", "ex-Square team"],
  ["LATE 2017", "Net 60 + free returns", "retailers buy free"],
  ["+3 MONTHS", "$100K → $1M", "monthly GMV, 10×"],
  ["JAN 2018", "Credit shock", "losing money per order"],
  ["+6 MONTHS", "Limits fixed it", "defaults −90%"],
  ["OCT 2019", "$1B valuation", "50K retailers · 7K makers"],
];
const S10 = () => (
  <G n={10} eyebrow="HOW THE WINNERS DID IT · THE CLOSEST COMP" size={30}
    title={<>Faire broke the cold start with terms, <span className="lime">then fixed credit fast.</span></>}>
    <div style={{ position: "absolute", left: 43.2, top: 150, width: 873.6 }}>
      <div className="gw" style={{ position: "absolute", left: 7, top: 46, height: 1, background: "rgba(255,255,255,.16)", "--w": "840px", "--d": "320ms" }} />
      <Row style={{ alignItems: "flex-start" }}>
        {FAIRE.map(([d, t, s], i) => (
          <div key={d} className="an" style={{ width: 145.4, paddingRight: 16, "--d": `${360 + i * 95}ms` }}>
            <Mono size={9} color={i === 5 ? LIME : "#6f8178"}>{d}</Mono>
            <div style={{ width: 13, height: 13, borderRadius: 999, marginTop: 14, background: i === 5 ? LIME : i === 3 ? "#e8705f" : OFFWHITE }} />
            <div className="h" style={{ fontSize: 14, marginTop: 20, lineHeight: "17.6px" }}>{t}</div>
            <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 14 }}>{s}</div>
          </div>
        ))}
      </Row>
    </div>
    {[
      { x: 43.2, k: "VERQO COPIES", items: ["Net terms as the hook: financing in month 1–2", "Buyer side free, seller side pays", "Sellers bring their own buyers"], a: true },
      { x: 488, k: "VERQO DOES DIFFERENTLY", items: ["Credit limits + insurance from day 1", "Flat SaaS fee, no 15–25% commission", "Starts in BRND + a community we know"] },
    ].map((c, ci) => (
      <Card key={c.k} d={620 + ci * 110} accent={c.a} style={{ position: "absolute", left: c.x, top: 330, width: 428.8, height: 126, padding: "16px 18px" }}>
        <Mono size={9} color={c.a ? LIME : "#7d8f85"}>{c.k}</Mono>
        {c.items.map((t) => (
          <Row key={t} style={{ gap: 10, marginTop: 13 }}>
            <Dot c={c.a ? LIME : "#5f7068"} s={6} />
            <span style={{ fontSize: 11.5, color: "#dfe8e2" }}>{t}</span>
          </Row>
        ))}
      </Card>
    ))}
  </G>
);

/* 11 — the playbook */
const PLAY = [
  ["1", "One narrow wedge", "Brex · Toast", "BRND’s buyers + Jewish wholesale", "play"],
  ["2", "Free side brings volume", "Faire · Melio", "Buyers free, sellers pay", "ring"],
  ["3", "Terms hook, risk moat", "Faire", "Financing month 1–2, with limits", "octagon"],
  ["4", "Each user brings the other", "Melio · Faire Direct", "Sellers bring their buyers", "arc"],
  ["5", "Partners scale it", "Bill.com · Toast", "Insurance, CFO, 3PL partners", "hexagon"],
  ["6", "Land, expand, raise on one metric", "Ramp", "North star: monthly GMV", "arrowUp"],
];
const S11 = () => (
  <G n={11} shift={18} eyebrow="HOW THE WINNERS DID IT · THE PLAYBOOK" size={30}
    title={<>Six patterns repeat. <span className="lime">Verqo’s move on each.</span></>}>
    {PLAY.map(([n, t, who, move, g], i) => (
      <Card key={n} d={190 + i * 85} style={{ position: "absolute", left: 43.2 + (i % 3) * 295.2, top: 140 + Math.floor(i / 3) * 176, width: 283.2, height: 160 }}>
        <Row style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <Glyph kind={g} size={34} d={300 + i * 85} />
          <NumBadge n={n} d={340 + i * 85} />
        </Row>
        <div className="h" style={{ fontSize: 15, marginTop: 14, width: 230, lineHeight: "19px" }}>{t}</div>
        <Mono size={9.5} color="#6f8178" style={{ marginTop: 9 }}>{who}</Mono>
        <Row style={{ position: "absolute", left: 20, right: 20, bottom: 18, gap: 9, alignItems: "flex-start" }}>
          <span style={{ width: 3, height: 15, background: LIME, borderRadius: 2, flex: "0 0 auto" }} />
          <span style={{ fontSize: 11.5, fontWeight: 700, lineHeight: "15px" }}>{move}</span>
        </Row>
      </Card>
    ))}
  </G>
);

/* 12 — acquisition channels */
const CHANNELS = [
  ["Insurance partners", "we bring them business", "intros to their customers", "CAC ~$250 · 5 live by M24", "octagon"],
  ["Shipping partners", "more freight volume", "their shippers onboard", "CAC ~$400 · 4 live by M24", "cube"],
  ["Warehouse & 3PL", "more brands to store", "their brands onboard", "CAC ~$400 · 8 live by M24", "hexagon"],
  ["CFOs & accountants", "25% of SaaS for 2 years", "their clients onboard", "25% for 2 yrs · 30 live by M24", "note"],
  ["Lending brokers", "borrowers with clean data", "buyers who need terms", "CAC ~$1,000 · 4 live by M24", "flag"],
];
const S12 = () => (
  <G n={12} shift={22} eyebrow="ACQUISITION CHANNELS · PARTNER-LED" size={28}
    title={<>Partners are the engine. <span className="lime">Sales, marketing and shows add fuel.</span></>}>
    <Row style={{ position: "absolute", left: 43.2, top: 150, gap: 10 }}>
      {CHANNELS.map(([t, they, we, cac, g], i) => (
        <Card key={t} d={190 + i * 95} accent={i === 3} style={{ width: 166.3, height: 184, padding: "14px 14px" }}>
          <Glyph kind={g} size={26} d={300 + i * 95} />
          <div className="h" style={{ fontSize: 13, marginTop: 11, lineHeight: "16px" }}>{t}</div>
          <Mono size={7.5} color="#6f8178" style={{ marginTop: 10 }}>THEY GET</Mono>
          <div style={{ fontSize: 10, color: "#cfdcd4", marginTop: 3, lineHeight: "13px" }}>{they}</div>
          <Mono size={7.5} color="#6f8178" style={{ marginTop: 8 }}>WE GET</Mono>
          <div style={{ fontSize: 10, color: "#cfdcd4", marginTop: 3, lineHeight: "13px" }}>{we}</div>
          <div className="m" style={{ position: "absolute", left: 14, right: 14, bottom: 11, fontSize: 8.5, color: i === 3 ? LIME : "#7d8f85", letterSpacing: "0.06em", lineHeight: "11px" }}>{cac}</div>
        </Card>
      ))}
    </Row>
    {/* every channel feeds the one engine */}
    {CHANNELS.map((c, i) => (
      <div key={c[0]} className="an" style={{ position: "absolute", left: 113 + i * 176.3, top: 340, "--d": `${640 + i * 70}ms` }}>
        <svg width="14" height="22" viewBox="0 0 14 22" aria-hidden="true">
          <path d="M7 0 v13 M1.5 9.5 L7 16 L12.5 9.5" fill="none" stroke={LIME} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
        </svg>
      </div>
    ))}
    <Card d={760} accent style={{ position: "absolute", left: 43.2, top: 364, width: 873.6, height: 50, padding: "0 22px", display: "flex", alignItems: "center", gap: 16 }}>
      <Mono size={9} color={LIME}>MAIN ENGINE</Mono>
      <span className="h" style={{ fontSize: 15 }}>Each partner introduces sellers, and each seller brings its buyers, free.</span>
    </Card>
    <div style={{ position: "absolute", left: 43.2, top: 434 }}>
      <Mono size={9} color="#7d8f85">PLUS</Mono>
    </div>
    <Row style={{ position: "absolute", left: 103.8, top: 420, width: 813, alignItems: "flex-start" }}>
      {[["Direct sales", "AE #1 M4 · AE #2 M13", "arrowRight"], ["Marketing spend", "$64K Y1 · $180K Y2", "bolt"], ["Expos & trade shows", "Cosmoprof · Natural Products Expo West", "star"]].map(([t, s, g], i) => (
        <Row key={t} className="an" style={{ width: 271, gap: 11, alignItems: "flex-start", "--d": `${820 + i * 90}ms` }}>
          <Glyph kind={g} size={22} d={860 + i * 90} style={{ flex: "0 0 auto", marginTop: 1 }} />
          <div>
            <div className="h" style={{ fontSize: 12 }}>{t}</div>
            <div className="m" style={{ fontSize: 9.5, color: "#8ea096", marginTop: 7, letterSpacing: "0.05em", lineHeight: "12px" }}>{s}</div>
          </div>
        </Row>
      ))}
    </Row>
  </G>
);

/* 13 — pricing */
const PLANS = [
  { k: "STARTER", p: "$99", b: "2 buyers incl. · $30 each extra", share: "36% of sellers", top: 334.6 },
  { k: "GROWTH", p: "$599", b: "10 buyers incl. · $25 each extra", share: "46% of sellers", top: 287.8 },
  { k: "PRO", p: "$1,499", b: "50 buyers incl. · $20 each extra", share: "15% of sellers", top: 241.0 },
  { k: "ENTERPRISE", p: "$3,999", b: "100 buyers incl. · $15 each extra", share: "3% of sellers", top: 194.2 },
];
const ORDERFEES = [
  ["Instant payout", "0.3–0.5% of payout"],
  ["Net-60/90 financing", "0.25% per 30 days"],
  ["Invoice financing", "0.8% spread"],
  ["Shipping", "5% markup"],
  ["Insurance", "7% of premium"],
  ["Card payments", "3–4% surcharge"],
];
const S13 = () => (
  <G n={13} shift={22} eyebrow="PRICING · LAND AND EXPAND" size={26}
    title={<>Sellers pay a flat plan. Buyers are free. <span className="lime">The money follows the order.</span></>}>
    <Row className="an" style={{ position: "absolute", left: 57.6, top: 138, gap: 10, alignItems: "baseline", "--d": "180ms" }}>
      <Mono size={8.5} color="#7d8f85">BUYERS</Mono>
      <span className="h lime" style={{ fontSize: 16 }}>$0, always</span>
    </Row>
    {PLANS.map((p, i) => (
      <div
        key={p.k}
        className={`${i === 3 ? "vpd-card-a" : "vpd-card"} an`}
        style={{ position: "absolute", left: 43.2 + i * 131, top: p.top - 16, width: 118, height: 468 - p.top + 16, padding: "14px 13px", boxSizing: "border-box", "--d": `${240 + i * 120}ms` }}
      >
        <Mono size={8} color={i === 3 ? LIME : "#7d8f85"} ls="0.1em">{p.k}</Mono>
        <Row style={{ gap: 4, alignItems: "baseline", marginTop: 9 }}>
          <span className="h" style={{ fontSize: 24 }}>{p.p}</span>
          <span style={{ fontSize: 10, color: "#8ea096" }}>/mo</span>
        </Row>
        <div style={{ fontSize: 9.5, color: "#9fb0a6", marginTop: 14, lineHeight: "13px" }}>{p.b}</div>
        <div className="m" style={{ position: "absolute", bottom: 14, fontSize: 8.5, color: i === 3 ? LIME : "#7d8f85", letterSpacing: "0.08em" }}>{p.share}</div>
      </div>
    ))}
    <div style={{ position: "absolute", left: 604.8, top: 128, width: 312 }}>
      <Mono size={9} color={LIME}>THEN, ON EVERY ORDER</Mono>
      {ORDERFEES.map(([t, v], i) => (
        <Row key={t} className="an" style={{ justifyContent: "space-between", height: 44.6, marginTop: i ? 0 : 14, borderBottom: "1px solid rgba(255,255,255,.06)", "--d": `${380 + i * 85}ms` }}>
          <span style={{ fontSize: 11.5, color: "#dfe8e2" }}>{t}</span>
          <span style={{ fontSize: 10.5, color: "#9fb0a6" }}>{v}</span>
        </Row>
      ))}
    </div>
  </G>
);

export const PART_A = [S1, S2, S3, S4, S5, S6, S7, S8, S9, S10, S11, S12, S13];
